import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ShoppingCart } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ordenesCompraService } from "@/services/ordenes-compra.service";
import type { OrdenCompraRow } from "@/types";
import { formatCLP } from "@/lib/mock-data";

function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-CL", { timeZone: "America/Santiago" });
}

function Fila({ oc, sugerida }: { oc: OrdenCompraRow; sugerida?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
      <div className="min-w-0">
        <Link
          to="/ordenes-compra/$codigo"
          params={{ codigo: oc.codigo }}
          className="text-sm font-medium text-primary hover:underline"
        >
          {oc.codigo}
        </Link>
        <div className="text-xs text-muted-foreground line-clamp-1">{oc.proveedorNombre ?? "—"}</div>
        <div className="text-xs text-muted-foreground">{fmtFecha(oc.fechaEnvio)}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-semibold tabular-nums">
          {oc.totalCLP != null ? formatCLP(oc.totalCLP) : "—"}
        </div>
        {sugerida && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            sugerida {oc.vinculoScore ?? 0}%
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Muestra las órdenes de compra asociadas a una licitación adjudicada/cerrada.
 * No se renderiza si no hay OC vinculadas ni sugeridas.
 */
export function OrdenesCompraCard({ codigo }: { codigo: string }) {
  const q = useQuery({
    queryKey: ["ordenes-compra", "por-licitacion", codigo],
    queryFn: () => ordenesCompraService.porLicitacion(codigo),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (q.isLoading) return <Skeleton className="h-24 w-full" />;
  const data = q.data;
  if (!data || ((data.vinculadas?.length ?? 0) === 0 && (data.sugeridas?.length ?? 0) === 0))
    return null;


  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold flex items-center gap-1.5">
          <ShoppingCart className="size-4" />
          Órdenes de compra asociadas
        </h2>
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatCLP(data.totalEmitidoCLP)} emitidos
        </span>
      </div>
      {data.vinculadas.map((oc) => (
        <Fila key={oc.id} oc={oc} />
      ))}
      {data.sugeridas.length > 0 && (
        <>
          <p className="text-xs text-muted-foreground pt-1">
            Posibles coincidencias detectadas automáticamente (sin código oficial de licitación):
          </p>
          {data.sugeridas.map((oc) => (
            <Fila key={oc.id} oc={oc} sugerida />
          ))}
        </>
      )}
    </section>
  );
}
